import React, { useState, useEffect } from "react";
import axios from "axios";
import { TypeAnimation } from "react-type-animation";
import { useAuth0 } from '@auth0/auth0-react';
import "../css/BetaGatePage.css";
import Footer from "../components/atoms/footer";
import Okta_Dark from "../Images/Okta_Dark.png";
import Okta_Light from "../Images/Okta_Light.png";
import MicrosoftLight from "../Images/Microsoft_Light.png";
import MicrosoftDark from "../Images/Microsoft_Dark.png";

interface BetaGatePageProps {
  onSuccessfulReferral?: () => void;
}

interface Navigator {
  userAgentData?: {
    platform: string;
    mobile: boolean;
    brands: Array<{ brand: string; version: string }>;
  };
}

export default function BetaGatePage({
  onSuccessfulReferral,
}: BetaGatePageProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { loginWithRedirect } = useAuth0();

  // ⭐ OS detection function
  const getOS = () => {
    const nav = navigator as any;
    if (nav.userAgentData?.platform) {
      const platform = nav.userAgentData.platform.toLowerCase();
      if (platform.includes('mac')) return 'macos';
      if (platform.includes('win')) return 'windows';
      if (platform.includes('linux')) return 'linux';
    }
    
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('mac')) return 'macos';
    if (ua.includes('win')) return 'windows';
    if (ua.includes('linux')) return 'linux';
    
    return 'unknown';
  };
  
  const handleLogin = () => {
    const os = getOS();
    window.location.href = `https://www.enlive.inc/login?os=${os}`;
  };

  const [theme, setTheme] = useState<"light" | "dark">("light");

  useEffect(() => {
    const target = document.documentElement;

    const updateTheme = () => {
      const isDark =
        target.getAttribute("data-theme") === "dark" ||
        target.classList.contains("dark");
      setTheme(isDark ? "dark" : "light");
    };

    updateTheme();

    const observer = new MutationObserver(() => {
      updateTheme();
    });

    observer.observe(target, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    axios
      .get("/check-auth", { withCredentials: true })
      .then((res) => {
        if (res.data && res.data.authenticated) {
          setIsAuthenticated(true);
        }
      })
      .catch((err) => {
        console.error("Auth check error:", err);
      });
  }, []);

  return (
    <>
      <div className="beta-gate-container">
        <h1 className="beta-gate-title">Welcome to our early access Beta!</h1>

        {/* ⭐ TypeAnimation with FADE effect using callbacks */}
        <div className="typing-animation-container">
          <TypeAnimation
            sequence={[
              // First message
              (el) => el?.classList.add('fade-in'),
              'Use many AI tools to do more complex tasks.',
              3000, // Display for 3 seconds
              (el) => el?.classList.add('fade-out'),
              500, // Fade out duration
              (el) => {
                el?.classList.remove('fade-out');
                el?.classList.remove('fade-in');
              },
              '', // Clear text
              
              // Second message
              (el) => el?.classList.add('fade-in'),
              'Get better AI responses with less follow up.',
              3000,
              (el) => el?.classList.add('fade-out'),
              500,
              (el) => {
                el?.classList.remove('fade-out');
                el?.classList.remove('fade-in');
              },
              '',
              
              // Third message
              (el) => el?.classList.add('fade-in'),
              'Shared memory across your files, emails, and chats.',
              3000,
              (el) => el?.classList.add('fade-out'),
              500,
              (el) => {
                el?.classList.remove('fade-out');
                el?.classList.remove('fade-in');
              },
              '',

              // Fourth message
              (el) => el?.classList.add('fade-in'),
              'Delete your profile connections anytime.',
              3000,
              (el) => el?.classList.add('fade-out'),
              500,
              (el) => {
                el?.classList.remove('fade-out');
                el?.classList.remove('fade-in');
              },
              ''
            ]}
            wrapper="div"
            speed={55} // 30 was too slow, 60 was too fast...
            deletionSpeed={99}
            omitDeletionAnimation={true}
            cursor={false}
            repeat={Infinity}
            style={{ fontSize: '2.5rem', display: 'inline-block' }}
            className="typing-animation-text"
          />
        </div>

        <div className="beta-gate-lower">
          <h2
            className={`beta-gate-waitlist ${
              isAuthenticated ? "beta-gate-waitlist-confirmed" : ""
            }`}
          >
            {isAuthenticated
              ? "You have been added to the waitlist, we'll send you an email when your spot is ready!"
              : "Get on the Waitlist!"}
          </h2>

          {!isAuthenticated && (
            <div className="beta-gate-login">
              <div className="sub-beta-login">
                <div className="sub-beta-login-btn">
                  <button
                    className="beta-gate-login-btn"
                    onClick={handleLogin}
                  >
                    Get Started
                  </button>
                </div>

                <div
                  className="beta-gate-trust-banner"
                  aria-label="Hosting and security providers"
                >
                  <div className="beta-gate-trust-item beta-gate-trust-item-hosting">
                    <span>Hosted by</span>
                    <div className="microsoft">
                      <img
                        src={theme === "light" ? MicrosoftDark : MicrosoftLight}
                        alt="Microsoft"
                        id="microsoft-logo"
                      />
                    </div>
                  </div>

                  <div className="beta-gate-trust-divider" aria-hidden="true" />

                  <div className="beta-gate-trust-item beta-gate-trust-item-security">
                    <span>Secured by</span>
                    <img
                      src={theme === "light" ? Okta_Dark : Okta_Light}
                      alt="Okta"
                      className="okta-logo"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="beta-gate-footer-shell">
        <Footer />
      </div>
    </>
  );
}